import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, useQuery, gql } from '@apollo/client'
import { saveQorConfiguration, getQorConfig } from '../../../apollo'
import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
  FormControlLabel, Switch
} from '@mui/material'
import PercentIcon from '@mui/icons-material/Percent'

const SAVE_FEES = gql`${saveQorConfiguration}`
const GET_FEES = gql`${getQorConfig}`

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }
const labelSx = { fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }

// The default commission by who fulfils the order (migration 126) — also the
// Order Percentage in Schedule A of the merchant agreement.
const RATES = [
  ['deliveryCommissionRate', 'We deliver', '20'],
  ['selfDeliveryCommissionRate', 'Restaurant delivers', '10'],
  ['pickupCommissionRate', 'Pickup', '10'],
]

/**
 * The split settings every order uses, whichever gateway takes the payment.
 * They are stored on the QorCommerce config row (qor_config), which is why
 * they save through saveQorConfiguration, but Stripe orders split with them too.
 */
function PlatformFees() {
  const { data } = useQuery(GET_FEES)
  const cfg = data?.qorConfig

  const [rates, setRates] = useState(() => Object.fromEntries(RATES.map(([k, , d]) => [k, d])))
  const [flatFee, setFlatFee] = useState('0.99')
  const [platformKeepsTax, setKeepsTax] = useState(true)
  const [hydrated, setHydrated] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  // Hydrate once the query resolves.
  React.useEffect(() => {
    if (cfg && !hydrated) {
      setRates(Object.fromEntries(RATES.map(([k, , d]) => [k, String(cfg[k] ?? d)])))
      setFlatFee(String(cfg.flatFee ?? '0.99'))
      setKeepsTax(cfg.platformKeepsTax !== false)
      setHydrated(true)
    }
  }, [cfg, hydrated])

  const [mutate, { loading }] = useMutation(SAVE_FEES, {
    onCompleted: () => {
      setSuccess('Platform fees saved!')
      setError('')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: () => {
      setError('Failed to save configuration.')
      setSuccess('')
      setTimeout(() => setError(''), 3000)
    }
  })

  const handleSave = () => {
    const parsed = Object.fromEntries(RATES.map(([k]) => [k, parseFloat(rates[k])]))
    const fee = parseFloat(flatFee)
    if (Object.values(parsed).some(rate => Number.isNaN(rate) || rate < 0 || rate > 100)) {
      setError('Commission rates must be between 0 and 100.')
      setTimeout(() => setError(''), 3000)
      return
    }
    // Same range the per-restaurant fee accepts, so a fallback can't be set to
    // something a restaurant row would refuse.
    if (Number.isNaN(fee) || fee < 0 || fee > 20) {
      setError('Service fee must be between 0 and 20.')
      setTimeout(() => setError(''), 3000)
      return
    }
    mutate({ variables: { configurationInput: { ...parsed, flatFee: fee, platformKeepsTax } } })
  }

  return (
    <Box sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #422006 60%, #a16207 100%)', px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PercentIcon sx={{ color: '#fde68a', fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>Platform Fees</Typography>
      </Box>

      <Box sx={{ p: 2.5, bgcolor: '#fff' }}>
        <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
          The starting point for every restaurant, whichever gateway takes the payment.
          Each one can be set per restaurant on the Commission Rates page, and that wins.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography sx={labelSx}>Default commission (% of subtotal)</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {RATES.map(([k, label, d]) => (
              <Box key={k} sx={{ flex: '1 1 120px' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.4 }}>{label}</Typography>
                <TextField fullWidth size="small" type="number" placeholder={d}
                  value={rates[k]} onChange={e => setRates(x => ({ ...x, [k]: e.target.value }))} sx={fieldSx} />
              </Box>
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
            Used only when a restaurant has no rate set; per-restaurant rates are on the
            Commission Rates page. These are also the Order Percentage in the merchant
            agreement (Signup Standards), and a restaurant approved through sign-up
            starts on the rates it signed.
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography sx={labelSx}>Fallback service fee (per order, customer-paid)</Typography>
          <TextField fullWidth size="small" type="number" placeholder="0.99"
            value={flatFee} onChange={e => setFlatFee(e.target.value)} sx={fieldSx} />
          <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
            Used only when a restaurant has no service fee of its own. Per-restaurant
            fees are managed on the Commission Rates page.
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography sx={labelSx}>Fallback tax rule</Typography>
          <FormControlLabel
            control={<Switch checked={platformKeepsTax} onChange={e => setKeepsTax(e.target.checked)} />}
            label="RushmyFood retains the tax (off = tax goes to restaurant)"
            sx={{ display: 'block' }}
          />
          <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
            Used only for restaurants left on the default. Who keeps the tax is set
            per restaurant on the Commission Rates page.
          </Typography>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            disabled={loading}
            onClick={handleSave}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#a16207', '&:hover': { bgcolor: '#854d0e' } }}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default withTranslation()(PlatformFees)
